import type { ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { TenantDeactivatedPage } from './pages/TenantDeactivatedPage';
import { ConfigGate, SettingsPrefetcher } from './providers/ConfigProvider';
import { SessionProvider } from './providers/SessionProvider';
import { TenantProvider } from './providers/TenantProvider';
import { AppRoutes } from './routes';
import { useTenantLifecycleStore } from './stores/tenant-lifecycle';

/**
 * Renders the "account deactivated" page instead of the app once the API has
 * returned 403 TENANT_DEACTIVATED (TDEV-571 A4). One-way for the tab's lifetime:
 * a deactivated tenant can't reactivate itself, so there's no un-set.
 */
function TenantDeactivatedGate({ children }: { children: ReactNode }) {
  const deactivated = useTenantLifecycleStore((s) => s.deactivated);
  return deactivated ? <TenantDeactivatedPage /> : <>{children}</>;
}

/**
 * Top-level providers. NOTE: `<SettingsProvider>` is intentionally NOT
 * mounted here — it is mounted inside `<ProtectedRoute>` (the single
 * canonical mount). Phase 6 (TDEV-266) turned `SettingsProvider` into a
 * side-effecty container that renders `<SettingsSync>`. Two mounts under
 * the same `QueryClientProvider` would produce two `SettingsSync` instances
 * subscribing to the same zustand stores, causing each user mutation to
 * fire two PATCHes and each server refetch to round-trip through the
 * hydration→PATCH path twice. See the `SettingsSync.test.tsx` "single
 * SettingsProvider mount invariant" describe block for the regression pin.
 */
export function App() {
  return (
    <TenantProvider>
      <BrowserRouter>
        <SessionProvider>
          <SettingsPrefetcher />
          <ConfigGate>
            <TenantDeactivatedGate>
              <AppRoutes />
            </TenantDeactivatedGate>
          </ConfigGate>
        </SessionProvider>
      </BrowserRouter>
    </TenantProvider>
  );
}
